import { mountStoreDevtool } from 'simple-zustand-devtools';
import { create } from 'zustand';

import { MOTTO_FORM_NODES, ORDERED_FORM_NODES, V2_FORM_NODES } from '@src/constants';

interface FormNode {
	name: string;
	nameES: string;
	index: number;
	available: boolean;
	completed: boolean;
	headerTitle: string;
}

interface FormNodeConfiguration {
	sectionName: string;
	sectionNameES: string;
	enabledByDefault: boolean;
	headerTitle?: string;
}

const initFormNodes = (nodeConfigurations: FormNodeConfiguration[]) => {
	return nodeConfigurations.map((node, index) => ({
		index,
		name: node.sectionName,
		nameES: node.sectionNameES,
		headerTitle: node.headerTitle ?? node.sectionName,
		available: node.enabledByDefault,
		completed: false,
	}));
};

const formNodes = initFormNodes(ORDERED_FORM_NODES as FormNodeConfiguration[]);

type State = {
	formNodes: FormNode[];
	activeSectionIndex: number;
	maxIndexReached: number;
	lockForm: boolean;
	isInitialized: boolean;
	activeSectionName: () => string;
	availableFormNodes: () => { name: string; nameES: string; index: number; completed: boolean }[];
};

type Actions = {
	initializeFormNodes: (flowOrder: string) => void;
	completeActiveNode: () => void;
	setActiveSection: (index: number) => void;
	stepBackwards: () => void;
	setGuarantorInfoNeeded: (guarantorNeeded: boolean) => void;
	lockSections: () => void;
};

const useFormSectionStore = create<State & Actions>((set, get) => ({
	formNodes,
	activeSectionIndex: 0,
	maxIndexReached: 0,
	lockForm: false,
	isInitialized: false,
	availableFormNodes: () => {
		return get()
			.formNodes.filter((node) => node.available)
			.map((node) => ({ name: node.name, nameES: node.nameES, index: node.index, completed: node.completed }));
	},
	activeSectionName: () => get().formNodes[get().activeSectionIndex].name,
	initializeFormNodes: (flowOrder = 'STANDARD') => {
		const mottoFlow = flowOrder === 'ClearAligners';
		const denturesFlow = flowOrder === 'Dentures';
		const v2Flow = flowOrder === 'V2';
		let selectedNodes: FormNodeConfiguration[];
		if (v2Flow) {
			selectedNodes = V2_FORM_NODES as FormNodeConfiguration[];
		} else if (mottoFlow || denturesFlow) {
			selectedNodes = MOTTO_FORM_NODES as FormNodeConfiguration[];
		} else {
			selectedNodes = ORDERED_FORM_NODES as FormNodeConfiguration[];
		}
		const determinedNodes = initFormNodes(selectedNodes);

		set({ formNodes: determinedNodes, isInitialized: true, activeSectionIndex: 0, maxIndexReached: 0 });

		return;
	},
	completeActiveNode: () => {
		const activeNodeIndex = get().activeSectionIndex;

		const updatedNodes = get().formNodes.map((node) => {
			if (node.index === activeNodeIndex) {
				// mark active section as complete
				return { ...node, completed: true };
			} else {
				return node;
			}
		});

		set({ formNodes: updatedNodes });

		const nextAvailableNode = get()
			.formNodes.slice(activeNodeIndex + 1)
			.filter((node) => node.available)[0];

		set({ activeSectionIndex: nextAvailableNode.index });

		if (nextAvailableNode.index > get().maxIndexReached) {
			set({ maxIndexReached: nextAvailableNode.index });
		}
	},
	// Call this action to navigate to already completed sections
	setActiveSection: (index: number) => {
		if (index >= get().formNodes.length) {
			throw new Error(`Error: ${index} is out of range for setActiveSection()`);
		}

		if (index > get().maxIndexReached) {
			throw new Error('Error: setActiveSection() should only be called on reached indexes');
		}

		set({ activeSectionIndex: index });
	},
	stepBackwards: () => {
		const currentIndex = get().activeSectionIndex;

		if (currentIndex) {
			set({ activeSectionIndex: currentIndex - 1 });
		}
	},
	setGuarantorInfoNeeded: (guarantorNeeded: boolean) => {
		const modifiedNodes = get().formNodes.map((formNode) => {
			if (formNode.name === 'Guarantor Information') {
				return { ...formNode, available: guarantorNeeded };
			}
			return formNode;
		});

		set({ formNodes: modifiedNodes });
	},
	lockSections: () => {
		set({ lockForm: true });
	},
}));

if (process.env.NODE_ENV === 'development') {
	mountStoreDevtool('formSectionStore', useFormSectionStore);
}

export default useFormSectionStore;
